import { bridgeV2ApiInstance, routes } from "@/services";
import { mapUnknownToAxiosError } from "@/utils";
import { useEffect, useState } from "react";
import { CreateHoasPaymentDetailsInput, UpdateHoasPaymentDetailsInput } from "@l4s/client-property-models";

import { HoasPaymentDetails } from "@/types/PropertyManagement";

export interface UseHoasPaymentDetails {
  loading: boolean;
  hoasPaymentDetails: Array<HoasPaymentDetails>;
  createHoaPaymentDetail: (input: CreateHoasPaymentDetailsInput) => Promise<void>;
  updateHoaPaymentDetail: (input: UpdateHoasPaymentDetailsInput) => Promise<void>;
  reloadHoasPaymentDetails: () => Promise<void>;
  error: string | undefined;
}

export const useHoasPaymentDetails = (propertyId: string): UseHoasPaymentDetails => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hoasPaymentDetails, setHoasPaymentDetails] = useState<Array<HoasPaymentDetails>>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  const getHoasPaymentDetails = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await bridgeV2ApiInstance.get<Array<HoasPaymentDetails>>(routes.GET_HOAS_PAYMENT_DETAILS, {
        params: { propertyId },
      });
      setHoasPaymentDetails(response.data);
    } catch (e) {
      const err = mapUnknownToAxiosError(e);
      setError(err.response.data.message || "Error fetching application property hoa payment details.");
    }
    setLoading(false);
  };

  const createHoaPaymentDetail = async (input: CreateHoasPaymentDetailsInput): Promise<void> => {
    try {
      const response = await bridgeV2ApiInstance.post<HoasPaymentDetails>(routes.CREATE_HOA_PAYMENT_DETAIL, {
        ...input,
      });
      setHoasPaymentDetails((existingHoaPaymentDetails) => [...existingHoaPaymentDetails, response.data]);
    } catch (e) {
      const err = mapUnknownToAxiosError(e);
      setError(err.response.data.message || "Error creating hoa payment detail.");
    }
  };

  const updateHoaPaymentDetail = async (input: UpdateHoasPaymentDetailsInput): Promise<void> => {
    try {
      const response = await bridgeV2ApiInstance.put<HoasPaymentDetails>(routes.UPDATE_HOA_PAYMENT_DETAIL(input.id), input);
      setHoasPaymentDetails((existingHoaPaymentDetails) =>
        existingHoaPaymentDetails.map((hoaPaymentDetail) =>
          hoaPaymentDetail.id === response.data.id ? { ...hoaPaymentDetail, ...response.data } : hoaPaymentDetail
        )
      );
    } catch (e) {
      const err = mapUnknownToAxiosError(e);
      setError(err.response.data.message || "Error updating hoa payment detail.");
    }
  };

  useEffect(() => {
    if (propertyId) {
      getHoasPaymentDetails();
    }
  }, [propertyId]);

  return {
    loading,
    hoasPaymentDetails,
    createHoaPaymentDetail,
    updateHoaPaymentDetail,
    reloadHoasPaymentDetails: getHoasPaymentDetails,
    error,
  };
};
